import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Reveal } from '../motion/Reveal';
import { pressable } from '../motion/variants';
import { HardHat, Car, TrafficCone, PhoneOff } from 'lucide-react';

const TOPICS = [
    {
        icon: HardHat,
        title: 'Wear a Helmet',
        summary: 'A helmet cuts the risk of a fatal head injury by up to 40%. The smartest riders never skip it.',
    },
    {
        icon: Car,
        title: 'Buckle Your Seat Belt',
        summary: 'Seat belts reduce serious injury risk by about 50%. Front and back — every seat, every trip.',
    },
    {
        icon: TrafficCone,
        title: 'Respect the Signal',
        summary: 'Stopping on red protects pedestrians and cross traffic. A few seconds saved is never worth a life.',
    },
    {
        icon: PhoneOff,
        title: 'Put the Phone Away',
        summary: 'Texting takes your eyes off the road for ~5 seconds — enough to cross a junction blind.',
    },
];

/** Road-safety education cards. */
export default function RoadSafety() {
    const reduce = useReducedMotion();

    return (
        <section
            id="road-safety"
            className="scroll-mt-20 py-20 sm:py-24 bg-background"
            aria-labelledby="safety-title"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal className="text-center max-w-2xl mx-auto mb-14">
                    <p className="overline mb-2">Know the why</p>
                    <h2 id="safety-title" className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">
                        Road safety, simply put
                    </h2>
                    <p className="mt-3 text-muted-foreground text-lg">
                        Good traffic sense is a habit anyone can build. Here’s what makes the biggest difference.
                    </p>
                </Reveal>

                <Reveal stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {TOPICS.map(({ icon: Icon, title, summary }) => (
                        <Reveal.Item key={title}>
                            <motion.article
                                {...(reduce ? {} : pressable)}
                                className="group h-full rounded-2xl bg-card border shadow-sm p-6 hover:border-primary/40 hover:shadow-lg transition-colors cursor-default"
                            >
                                <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center mb-4 group-hover:bg-success/20 transition-colors">
                                    <Icon className="w-6 h-6 text-success" aria-hidden="true" />
                                </div>
                                <h3 className="font-heading font-semibold text-lg">{title}</h3>
                                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{summary}</p>
                            </motion.article>
                        </Reveal.Item>
                    ))}
                </Reveal>
            </div>
        </section>
    );
}
